import React from "react";
import {Box, Chip, Divider, Stack, Typography} from "@mui/material";
import {formatReais} from "../utils/formatters";

// Valor de uma linha: o desconto vem negativo do cálculo único e aparece com o sinal de menos.
const valorDaLinha = (valor) => {
    const numero = Number(valor);
    if (Number.isNaN(numero)) return '-';
    return numero < 0 ? `− ${formatReais(-numero)}` : formatReais(numero);
};

/*
 * Linhas de uma seção da fatura, como o cálculo único devolve (BBF-24): o que é, a explicação e o valor. A prévia
 * da empresa, a da assinatura e o simulador do plano usam o mesmo componente, porque usam o mesmo cálculo.
 */
export function LinhasDaComposicao({linhas = []}) {
    return (
        <Stack divider={<Divider flexItem />} spacing={0}>
            {linhas.map((linha, index) => {
                const negativa = Number(linha.valor) < 0;
                return (
                    <Box key={`${linha.tipo}-${linha.o_que}-${index}`} sx={{display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 2, py: 1.25}}>
                        <Typography variant="body2" fontWeight={600}>{linha.o_que}</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{textAlign: 'right', whiteSpace: 'nowrap', color: negativa ? 'success.main' : 'text.primary', fontVariantNumeric: 'tabular-nums'}}>
                            {valorDaLinha(linha.valor)}
                        </Typography>
                        {linha.explicacao ? (
                            <Typography variant="caption" color="text.secondary" sx={{gridColumn: '1 / -1'}}>{linha.explicacao}</Typography>
                        ) : null}
                    </Box>
                );
            })}
        </Stack>
    );
}

/*
 * A fatura inteira: uma seção por assinatura, as que ficam fora com o motivo, e o total. `rotuloDaSecao` e
 * `rotuloDeFora` dizem o nome de cada assinatura, que o cálculo não traz.
 */
export default function ComposicaoDaFatura({previa, rotuloDaSecao, rotuloDeFora, detalheDaSecao}) {
    const secoes = previa?.secoes || [];
    const fora = previa?.fora || [];

    if (!secoes.length && !fora.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
                Nenhuma assinatura vai para esta fatura.
            </Typography>
        );
    }

    return (
        <Stack spacing={2}>
            {secoes.map((secao) => (
                <Box key={secao.licenca_id} sx={{border: 1, borderColor: 'divider', borderRadius: 2, px: 2, pt: 1.5, pb: 0.5}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{mb: 0.5}}>
                        <Box sx={{minWidth: 0}}>
                            <Typography fontWeight={700}>{rotuloDaSecao ? rotuloDaSecao(secao) : `${secao.plano} v${secao.versao}`}</Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{mt: 0.5}}>
                                {secao.tipo === 'ENCERRAMENTO' ? <Chip size="small" label="Encerramento" color="warning" variant="outlined" /> : null}
                                {detalheDaSecao ? detalheDaSecao(secao) : null}
                            </Stack>
                        </Box>
                        <Typography fontWeight={700} sx={{whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>{formatReais(secao.total)}</Typography>
                    </Stack>
                    <LinhasDaComposicao linhas={secao.linhas} />
                </Box>
            ))}

            {fora.length ? (
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>Fora desta fatura</Typography>
                    <Stack spacing={0.5}>
                        {fora.map((item) => (
                            <Typography key={item.licenca_id || item.motivo} variant="body2" color="text.secondary">
                                {rotuloDeFora ? rotuloDeFora(item) : item.licenca_id}: {item.motivo}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            ) : null}

            <Stack direction="row" justifyContent="space-between" sx={{pt: 1, borderTop: 2, borderColor: 'divider'}}>
                <Typography fontWeight={700}>Total da fatura</Typography>
                <Typography fontWeight={700} sx={{fontVariantNumeric: 'tabular-nums'}}>{formatReais(previa?.total)}</Typography>
            </Stack>
        </Stack>
    );
}
